import React from 'react';
import { CheckCircleIcon, KeyIcon, UserIcon, ComputerDesktopIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Modal } from './Modal';
import { Badge } from './Badge';
import { formatDate } from '../../utils/formatters';

const SuccessNotification = ({ 
  isOpen, 
  onClose, 
  license, 
  client, 
  software 
}) => {
  if (!license) return null;


  const getLicenseTypeBadge = (license) => {
    // Usar un color basado en el ID del LicenseType para consistencia
    const typeId = license.license_type_config || 0;
    const colors = ['secondary', 'success', 'primary', 'warning', 'danger', 'info'];
    return colors[typeId % colors.length];
  };

  const getLicenseTypeLabel = (license) => {
    // Usar directamente el campo license_type que ahora contiene el nombre del tipo definido por el usuario
    return license.license_type || 'Sin tipo';
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      inactive: 'secondary',
      expired: 'danger',
      suspended: 'warning',
      cancelled: 'danger'
    };
    return variants[status] || 'secondary';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Activa',
      inactive: 'Inactiva',
      expired: 'Expirada',
      suspended: 'Suspendida',
      cancelled: 'Cancelada'
    };
    return labels[status] || status;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="¡Licencia Creada Exitosamente!"
      size="lg"
    >
      <div className="p-6">
        {/* Success Header */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <CheckCircleIcon className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Licencia Generada Correctamente
          </h3>
          <p className="text-sm text-gray-500">
            La licencia ha sido creada y está lista para ser utilizada
          </p>
        </div>

        {/* License Details */}
        <div className="space-y-6">
          {/* License Key Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <KeyIcon className="h-5 w-5 text-blue-600 mr-2" />
              <h4 className="text-sm font-medium text-gray-900">Clave de Licencia</h4>
            </div>
            <div className="bg-white border border-gray-200 rounded-md p-3">
              <code className="text-sm font-mono text-gray-900 break-all">
                {license.license_key}
              </code>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Guarda esta clave de forma segura. Se necesitará para activar el software.
            </p>
          </div>

          {/* License Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Client Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <UserIcon className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="text-sm font-medium text-gray-900">Cliente</h4>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900">{client?.name}</p>
                <p className="text-xs text-gray-600">{client?.email}</p>
                <p className="text-xs text-gray-600">{client?.company}</p>
              </div>
            </div>

            {/* Software Information */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <ComputerDesktopIcon className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="text-sm font-medium text-gray-900">Software</h4>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900">{software?.name}</p>
                <p className="text-xs text-gray-600">Versión {software?.version}</p>
                <p className="text-xs text-gray-600">{software?.description}</p>
              </div>
            </div>
          </div>

          {/* License Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Detalles de la Licencia</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Tipo:</span>
                  <Badge variant={getLicenseTypeBadge(license)}>
                    {getLicenseTypeLabel(license)}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Estado:</span>
                  <Badge variant={getStatusBadge(license.status)}>
                    {getStatusLabel(license.status)}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Instalaciones máximas:</span>
                  <span className="text-sm font-medium text-gray-900">{license.max_installations}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Precio:</span>
                  <span className="text-sm font-medium text-gray-900">
                    ${license.price || '0.00'}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Fecha de emisión:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(license.issued_date)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Vigencia:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {license.perpetual_validity || !license.expiry_date
                      ? 'Indefinida'
                      : formatDate(license.expiry_date)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Días restantes:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {license.perpetual_validity || !license.expiry_date
                      ? '—'
                      : license.days_remaining || 'N/A'}
                  </span>
                </div>
                {license.original_price && license.original_price !== license.price && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Precio original:</span>
                    <span className="text-sm text-gray-500 line-through">
                      ${license.original_price}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Promotion Information */}
          {(license.promotion_name || license.discount_percentage > 0) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Información de Promoción</h4>
              <div className="space-y-2">
                {license.promotion_name && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Promoción:</span>
                    <span className="text-sm font-medium text-gray-900">{license.promotion_name}</span>
                  </div>
                )}
                {license.discount_percentage > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Descuento:</span>
                    <span className="text-sm font-medium text-green-600">
                      {license.discount_percentage}%
                    </span>
                  </div>
                )}
                {license.promotion_description && (
                  <div className="mt-2">
                    <span className="text-sm text-gray-600">Descripción:</span>
                    <p className="text-sm text-gray-700 mt-1">{license.promotion_description}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {license.notes && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Notas</h4>
              <p className="text-sm text-gray-700">{license.notes}</p>
            </div>
          )}

          {/* Email Notification */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Se envió email al cliente
                </p>
                <p className="text-xs text-green-700 mt-1">
                  La información de la licencia ha sido enviada automáticamente a {client?.email}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
};

export { SuccessNotification };
